import { alpha, type Theme } from '@mui/material/styles';
import { BPS_BLACK } from '../../theme/theme-colors';

export const CHART_SERIES_BUTTONS_MAX_PER_ROW = 4;

const INACTIVE_SERIES_FILL_OPACITY = 0.12;
const INACTIVE_SERIES_HOVER_FILL_OPACITY = 0.2;

/** Grid container for chart series toggle buttons (max four per row). */
export function chartSeriesToolbarSx(seriesCount: number) {
  const columns = Math.min(Math.max(seriesCount, 1), CHART_SERIES_BUTTONS_MAX_PER_ROW);
  const compact = seriesCount > CHART_SERIES_BUTTONS_MAX_PER_ROW;
  const dense = seriesCount > 6;

  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gap: dense ? 0.375 : compact ? 0.5 : 1,
    width: '100%',
    boxSizing: 'border-box',
    px: 1,
    py: 0.5,
    justifyItems: 'stretch',
  };
}

function chartSeriesButtonSizing(seriesCount: number) {
  const compact = seriesCount > CHART_SERIES_BUTTONS_MAX_PER_ROW;
  const dense = seriesCount > 6;

  return {
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    px: dense ? 0.5 : compact ? 0.75 : 1.5,
    py: dense ? 0.25 : compact ? 0.375 : 0.5,
    fontSize: dense ? '0.65rem' : compact ? '0.72rem' : '0.8125rem',
    lineHeight: 1.25,
    textTransform: 'none' as const,
    textAlign: 'center' as const,
    whiteSpace: 'normal' as const,
    overflowWrap: 'break-word' as const,
    border: '1px solid transparent',
    boxSizing: 'border-box' as const,
  };
}

const chartSeriesButtonLabelSx = {
  '&& .MuiButton-label': {
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    lineHeight: 1.25,
    width: '100%',
  },
} as const;

/** Contained button styles that use a chart series color and override global MuiButton contained theme. */
export function chartSeriesButtonSx(color: string, seriesCount = 1, isActive = true) {
  const sizing = chartSeriesButtonSizing(seriesCount);

  if (isActive) {
    return {
      '&&': {
        ...sizing,
        backgroundColor: color,
        color: BPS_BLACK,
      },
      ...chartSeriesButtonLabelSx,
      '&&:hover': {
        backgroundColor: color,
        filter: 'brightness(0.9)',
      },
    };
  }

  return (theme: Theme) => ({
    '&&': {
      ...sizing,
      backgroundColor: alpha(color, INACTIVE_SERIES_FILL_OPACITY),
      color: theme.vars?.palette?.text?.secondary ?? theme.palette.text.secondary,
      borderColor: color,
    },
    ...chartSeriesButtonLabelSx,
    '&&:hover': {
      backgroundColor: alpha(color, INACTIVE_SERIES_HOVER_FILL_OPACITY),
      borderColor: color,
    },
  });
}
